import React, { useContext, useCallback, useState } from 'react';
import clientAvenyModel from '../../../../../Model/clientAvenyModel';
import { IClientBusinessModel } from '../../../../../Types/ClientModelTypes';
import { IBusinessCollectionProps } from '../../../../../Types/ObjektVy';
import { collectionCompToObjektVy } from '../../../_Common/Components/BusinessCollectionComponents/BusinessCollectionUtils';
import BusinessDataForm, {
  BusinessDataFormContext,
} from '../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataForm';
import { StandardRutinContext } from '../../../_StandardRutin/StandardRutinContext';
import ObjektVy from '../../../_Common/Components/BusinessCollectionComponents/ObjektVy/ObjektVy';
import ObjektVySida from '../../../_Common/Components/BusinessCollectionComponents/ObjektVySida/ObjektVySida';
import {
  StatistikrapporteringQuery,
  StatistikrapporteringQueryVariables,
  StatistikrapporteringQuery_node_TEDBOAktivitet,
} from './Queries/_QueryTypes/StatistikrapporteringQuery';
import query from './Queries/StatistikrapporteringQuery.gql';
import AttributGrupp from '../../../_Common/Components/BusinessCollectionComponents/AttributGrupp/AttributGrupp';
import EDTypography from '../../../../../BaseComponents/Typography/EDTypography';
import {
  TEDBOAktivitetTypEnum,
  TEDInputBOStatistikAntal,
} from '../../../../../globalQueryTypes';
import { useStatistikrapporteringFunktionsMenyItems } from '../../Rutiner/Statistikrapportering/Statistikrapportering';
import { useQuery, useMutation } from '@apollo/client';
import {
  StatistikrapporteringUpdateMutation,
  StatistikrapporteringUpdateMutationVariables,
} from './Mutations/_QueryTypes/StatistikrapporteringUpdateMutation';
import updateMutation from './Mutations/StatistikrapporteringUpdateMutation.gql';
import { ActiveDatabaseContext } from '../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import {
  StatistikrapporteringAldersindelningQuery,
  StatistikrapporteringAldersindelningQueryVariables,
} from './Queries/_QueryTypes/StatistikrapporteringAldersindelningQuery';
import alderQuery from './Queries/StatistikrapporteringAldersindelningQuery.gql';
import {
  IFormData,
  IFieldValidations,
  IValidateFn,
  defaultEditMenuCancelButton,
  defaultEditMenuSaveButton,
  IEditMenuActionParams,
  removeIdsFromNewAssociations,
} from '../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormUtils';
import { ModelContext } from '../../../../../Model/ModelContext/ModelContext';
import { getCurrentDateString } from '../../../../../Utils/dateUtils';

import { ObjektVyKort } from '../../../_Common/Components/BusinessCollectionComponents/ObjektVyKort/ObjektVyKort';
import { ThemeProvider, StyledEngineProvider, useTheme } from '@mui/material';
import AvenyTheme, {
  AvenyThemeDark,
} from '../../../../../BaseComponents/ThemeProvider/AvenyTheme';
import { aktivitetColors, themeColor } from '../../Utils/AktivitetTheme';
import { RutinSettingsContext } from '../../../_Common/Components/RutinAvailabilityContext/RutinAvailabilityContext';
import { selectedId } from '../../../../../Components/MasterDetail/MasterDetailUtils';
import {
  aktuellaAldrar,
  initAktGiltigaAldrar,
  selectValue,
} from './Utils/Utils';
import {
  validateDigitalStatistik,
  validateAlderStatistik,
  validateStandardStatistik,
} from './Utils/Validations';
import {
  attributeCounter,
  onDigitalCounterChange,
  onAttrCounterChange,
  alderCounters,
  alderInitAntal,
} from './Utils/Counters';
import { useSelectedDetailQuery } from '../../../../../Components/QueryComponents/SelectedDetailQuery/useSelectedDetailQuery';
import { AktivitetDateTimeFormatter } from '../Aktivitet/Utils/AktivitetDateTimeFormatter';
import { handleGQLError } from '../../../../../Utils/Error';
import Psalmer from '../Aktivitet/Components/Psalmer';

export const verksamhetstypLabelText = (verksamhetsTyp: string) => {
  switch (verksamhetsTyp) {
    case '6a':
      return 'Antal gånger individer fått besök';
    case '6b':
      return 'Antal gånger individer fått besök';
    case '6c':
      return 'Antal kontakttillfällen';
    case '7b':
      return 'Antal besökare';
    case '8s':
      return 'Antal besökstillfällen i skola/förskola';
    default:
      return undefined;
  }
};

export const verksamhetstypHintText = (verksamhetsTyp: string) => {
  switch (verksamhetsTyp) {
    case '6a':
      return 'Antal gånger individer fått besök i sina hem av frivilliga eller anställda medarbetare. Sorgehusbesök som förberedelse inför begravning räknas inte här, däremot besök efter begravningen med diakonalt syfte. Besöks en grupp av personer räknas varje person en gång.';
    case '6b':
      return 'Antal gånger individer fått besök då de vistats på boende eller institution, t.ex. sjukhus, vårdinrättning eller fängelse. Besöks en grupp av personer räknas varje person en gång. Sorgehusbesök som förberedelse inför begravning räknas inte här.';
    case '6c':
      return 'Antal tillfällen som personer tagit kontakt med församlingens medarbetare - personligen, via brev, telefon, e-post eller sociala medier - och efterfrågar råd, bistånd eller stöd, inklusive dem som sökt ekonomisk hjälp som inräknats under "Ekonomiska bidrag till hushåll".';
    default:
      return undefined;
  }
};

export const validAktTypeForKomm = [
  TEDBOAktivitetTypEnum.BEGRAVNING,
  TEDBOAktivitetTypEnum.VIGSEL,
];

export const validGtjCategoriForKomm = [
  '1a',
  '1b',
  '2a',
  '2c',
  '2f',
  '2g',
  '2h',
  '3c',
  '3e',
  '3g',
  '2e',
];

const statistikModel: IClientBusinessModel =
  clientAvenyModel.BO.BusinessModels.BOAktivitet;

interface IStatistikrapporteringVyProps extends IBusinessCollectionProps {
  isEditing?: boolean;
  onValuesCommited?: () => void;
  onCancel?: () => void;
  isInDialog?: boolean;
}

export const StatistikrapporteringVy: React.ComponentType<
  React.PropsWithChildren<IStatistikrapporteringVyProps>
> = props => {
  const { activeDatabase } = useContext(ActiveDatabaseContext);
  const { avenyCollectionMap } = useContext(ModelContext);
  const routineAvailabilityContext = useContext(RutinSettingsContext);
  const theme = useTheme();
  const standardRutinContext = useContext(StandardRutinContext);
  const isMobileOrTabletMode = props.config?.fullPage;
  const [statisticHaveBeenSaved, setStatisticHaveBeenSaved] =
    useState<boolean>(false);

  const {
    nodeData,
    loading: queryLoading,
    refetch: nodeRefetch,
  } = useSelectedDetailQuery<
    StatistikrapporteringQuery,
    StatistikrapporteringQuery_node_TEDBOAktivitet,
    StatistikrapporteringQueryVariables
  >(query, props.id);

  const { data: alderData, loading: queryAlderLoading } = useQuery<
    StatistikrapporteringAldersindelningQuery,
    StatistikrapporteringAldersindelningQueryVariables
  >(alderQuery, {
    variables: {
      dbname: activeDatabase || '',
      routine: routineAvailabilityContext.activeRoutine?.routine.rutinID || 0,
    },
    skip: queryLoading || !nodeData?.AldersindeladAktivitet,
  });

  const funktionsMenyItems = useStatistikrapporteringFunktionsMenyItems({
    id: selectedId(props.id),
    collection: statistikModel,
  });

  const aktType = nodeData?._typ;
  const digital = nodeData?.DigitalAktivitet || false;
  const aldersindelad = nodeData?.AldersindeladAktivitet || false;

  const aktGiltigaAldrar =
    aldersindelad && !queryAlderLoading && alderData?.db?.BOAldersindelning_All
      ? initAktGiltigaAldrar(nodeData, alderData?.db?.BOAldersindelning_All)
      : [];

  const verksamhetsKategori: string =
    (!queryLoading && nodeData?._Associations?.Verksamhetstyp?.CATEGORY) || '';
  const gudstjanstKategori: string =
    (!queryLoading && nodeData?._Associations?.GudstjanstTyp?.CATEGORY) || '';

  const disabled =
    !queryLoading &&
    !queryAlderLoading &&
    nodeData?.StatRapSenast < getCurrentDateString();

  const [
    updateMutationFn,
    { loading: updateMutationLoading, error: updateMutationError },
  ] = useMutation<
    StatistikrapporteringUpdateMutation,
    StatistikrapporteringUpdateMutationVariables
  >(updateMutation, {
    onCompleted: data => {
      if (statisticHaveBeenSaved) {
        standardRutinContext.selectNextDetail.current?.(true);
        setStatisticHaveBeenSaved(false);
      }
    },
    onError: handleGQLError,
  });

  const updateFunction = useCallback(
    (values: IFormData) => {
      const newValues = removeIdsFromNewAssociations(values);

      if (newValues._Associations?.AktPsalmer_Add_Values) {
        newValues._Associations.AktPsalmer_Add_Values =
          newValues._Associations.AktPsalmer_Add_Values.filter((v: any) =>
            Boolean(v._Associations)
          );
      }

      let newPsalmValues = { _Associations: {} };
      if (newValues?._Associations?.AktPsalmer_Add_Values) {
        newPsalmValues._Associations = {
          AktPsalmer_Add_Values: newValues._Associations.AktPsalmer_Add_Values,
        };
      }
      if (newValues?._Associations?.AktPsalmer_Update_Values) {
        newPsalmValues._Associations = {
          ...newPsalmValues._Associations,
          AktPsalmer_Update_Values:
            newValues._Associations.AktPsalmer_Update_Values,
        };
      }
      if (newValues?._Associations?.AktPsalmer_Delete_Values) {
        newPsalmValues._Associations = {
          ...newPsalmValues._Associations,
          AktPsalmer_Delete_Values:
            newValues._Associations.AktPsalmer_Delete_Values,
        };
      }

      let variables: StatistikrapporteringUpdateMutationVariables = {
        AktivitetsID: nodeData?.id || '',
        UpdatePsalmValues: {
          id: nodeData?.id ?? '',
          ...newPsalmValues,
          _version: nodeData?._version ?? 1,
        },
      };

      if (digital) {
        let oldvalues = nodeData?._Associations?.DigitalaVisningar;

        let digvalues =
          values._Associations?.DigitalaVisningar_Add_Values ??
          values._Associations?.DigitalaVisningar_Values;

        variables.Digital = {
          visningarTotal: selectValue(
            digvalues.VISNINGAR_TOTAL,
            oldvalues?.VISNINGAR_TOTAL
          ),
          facebook: selectValue(
            digvalues.VISNINGAR_FACEBOOK,
            oldvalues?.VISNINGAR_FACEBOOK
          ),
          youtube: selectValue(
            digvalues.VISNINGAR_YOUTUBE,
            oldvalues?.VISNINGAR_YOUTUBE
          ),
          vimeo: selectValue(
            digvalues.VISNINGAR_VIMEO,
            oldvalues?.VISNINGAR_VIMEO
          ),
          ovrigt: selectValue(
            digvalues.VISNINGAR_OVRIG,
            oldvalues?.VISNINGAR_OVRIG
          ),
        };
      } else {
        let statistik: TEDInputBOStatistikAntal = {
          antal: selectValue(values.ANTAL_BESOKARE, nodeData?.ANTAL_BESOKARE),
          barn: selectValue(values.ANTAL_BARN, nodeData?.ANTAL_BARN),
          tillfallen: selectValue(
            values.ANTAL_TILLFALLEN,
            nodeData?.ANTAL_TILLFALLEN
          ),
          kommunioner: selectValue(
            values.ANTAL_KOMMUNIONER,
            nodeData?.ANTAL_KOMMUNIONER
          ),
        };

        if (aldersindelad) {
          statistik.AlderIndelatKod = [];
          let aldrar = aktuellaAldrar(values, nodeData);
          for (let key in aldrar) {
            statistik.AlderIndelatKod.push({
              kod: key,
              antal: aldrar[key],
            });
          }
        }
        variables.Statistik = statistik;
      }

      return updateMutationFn({
        variables,
        refetchQueries: ['StatistikrapporteringQuery'],
      });
    },
    [nodeData, digital, updateMutationFn, aldersindelad]
  );

  const validateStatistik: IValidateFn<
    StatistikrapporteringQuery_node_TEDBOAktivitet
  > = ({ currentValues }): IFieldValidations => {
    if (digital) {
      return validateDigitalStatistik(
        currentValues,
        statistikModel,
        avenyCollectionMap
      );
    } else if (aldersindelad) {
      return validateAlderStatistik(
        currentValues,
        aktGiltigaAldrar,
        nodeData,
        statistikModel,
        avenyCollectionMap
      );
    } else {
      return validateStandardStatistik(
        currentValues,
        statistikModel,
        avenyCollectionMap
      );
    }
  };

  const [aktivitetColor, prelAktivitetBackground, preliminaryBorderColor] =
    aktivitetColors(nodeData?._typ, !!nodeData?.PRELBOKAD, false, theme);

  const FormattedDayTime = AktivitetDateTimeFormatter(
    nodeData?.FRANDATUM,
    nodeData?.TILLDATUM,
    nodeData?.FRANTID,
    nodeData?.TILLTID
  );

  const getSmallScreenButtons = () => {
    return [
      {
        ...defaultEditMenuCancelButton,
      },
      {
        ...defaultEditMenuSaveButton,
        onClick: (editMenuActionParams: IEditMenuActionParams) => {
          setStatisticHaveBeenSaved(true);
          defaultEditMenuSaveButton.onClick(editMenuActionParams);
        },
      },
    ];
  };

  const isBegravningAndIsSvKyrkanBegrType =
    nodeData?._typ === 'BEGRAVNING' && nodeData?.Begravningstyp === 'Sv kyrkan';

  const activityTypeHasPsalmer =
    nodeData?._typ === 'GUDSTJANST' ||
    nodeData?._typ === 'VIGSEL' ||
    nodeData?._typ === 'DOP' ||
    isBegravningAndIsSvKyrkanBegrType;

  return (
    <BusinessDataForm<StatistikrapporteringQuery_node_TEDBOAktivitet>
      editMenuActions={
        props.isInDialog
          ? [
              {
                ...defaultEditMenuCancelButton,
                onClick: ({ businessDataFormContext }) => {
                  businessDataFormContext?.formStateDispatch({ type: 'RESET' });
                },
              },
              {
                ...defaultEditMenuSaveButton,
                flat: true,
                icon: defaultEditMenuSaveButton.icon,
                hideIconIfNotFloating: true,
                onClick: editMenuActionParams => {
                  defaultEditMenuSaveButton.onClick(editMenuActionParams);
                },
              },
              {
                label: 'Avbryt',
                id: 'cancel',
                onClick: ({ businessDataFormContext, editMenuOnCancel }) => {
                  businessDataFormContext?.formStateDispatch({
                    type: 'CANCEL',
                  });
                  editMenuOnCancel?.();
                },
                icon: defaultEditMenuCancelButton.icon,
                hideIconIfNotFloating: true,
              },
            ]
          : isMobileOrTabletMode
          ? getSmallScreenButtons()
          : undefined
      }
      isEditing={props.isEditing}
      model={statistikModel}
      data={nodeData}
      fullScreen={props.config?.fullPage ?? false}
      updateMutation={updateFunction}
      mutationLoading={updateMutationLoading}
      mutationError={updateMutationError}
      validateFns={[validateStatistik as IValidateFn]}
      refetchData={nodeRefetch}
      dataLoading={queryLoading}
      onValuesCommited={props.onValuesCommited}
      onCancel={props.onCancel}
      customDataFormHasChanged={(initialValues, values) => {
        let hasChanged = false;
        if (values.aldrar) {
          hasChanged = Object.entries(values.aldrar).some(([key, value]) => {
            const initialValue = alderInitAntal(
              initialValues ?? undefined,
              key
            );
            const newValue = value;

            return initialValue !== newValue;
          });
          if (hasChanged) {
            return hasChanged;
          }
        }
        //jämför initiala värden med de uppdaterade
        if (values._Associations?.DigitalaVisningar_Values) {
          hasChanged = Object.entries(
            values._Associations.DigitalaVisningar_Values
          ).some(([key, value]) => {
            const initialValue =
              initialValues?._Associations?.DigitalaVisningar?.[key];
            const newValue = value;

            return initialValue !== newValue;
          });
          if (hasChanged) {
            return hasChanged;
          }
        }

        if (values._Associations?.AktPsalmer_Update_Values) {
          hasChanged = Object.entries(
            values._Associations.AktPsalmer_Update_Values
          ).some(([key, value]) => {
            const initialValue =
              initialValues?._Associations?.AktPsalmer_Update_Values?.[key];
            const newValue = value;

            return initialValue !== newValue;
          });
          if (hasChanged) {
            return hasChanged;
          }
        }

        if (values._Associations?.AktPsalmer_Add_Values) {
          hasChanged = Object.entries(
            values._Associations.AktPsalmer_Add_Values
          ).some(([key, value]) => {
            const initialValue =
              initialValues?._Associations?.AktPsalmer_Add_Values?.[key];
            const newValue = value;

            return initialValue !== newValue;
          });
          if (hasChanged) {
            return hasChanged;
          }
        }

        if (values._Associations?.AktPsalmer_Delete_Values) {
          hasChanged = Object.entries(
            values._Associations.AktPsalmer_Delete_Values
          ).some(([key, value]) => {
            const initialValue =
              initialValues?._Associations?.AktPsalmer_Delete_Values?.[key];
            const newValue = value;

            return initialValue !== newValue;
          });
          if (hasChanged) {
            return hasChanged;
          }
        }

        hasChanged = Object.entries(values).some(([key, value]) => {
          if (typeof value !== 'object' || value == null) {
            const initialValue = initialValues?.[key];
            const newValue = value;

            return initialValue !== newValue;
          } else {
            return false;
          }
        });

        return hasChanged;
      }}
    >
      <ObjektVy
        nodeLoading={queryLoading}
        funktionsMenyItems={funktionsMenyItems}
        editImplemented
      >
        <ObjektVySida
          caption="Statistikrapportering"
          sidNr={1}
          collectionName={statistikModel.CollectionName}
          smallScreenNoPadding
        >
          <BusinessDataFormContext.Consumer>
            {formContext => (
              <ObjektVyKort noPadding>
                <ObjektVyKort
                  background={prelAktivitetBackground}
                  backgroundColor={aktivitetColor}
                  cardInCard
                  border={
                    nodeData?.PRELBOKAD
                      ? `2px solid ${preliminaryBorderColor}`
                      : undefined
                  }
                >
                  <StyledEngineProvider injectFirst>
                    <ThemeProvider
                      theme={
                        themeColor(nodeData?._typ) ? AvenyThemeDark : AvenyTheme
                      }
                    >
                      <EDTypography variant="h5" color="black">
                        {nodeData?.Titel2}
                      </EDTypography>
                      <EDTypography variant="subtitle2" color="black">
                        {FormattedDayTime}
                      </EDTypography>
                      <EDTypography variant="subtitle2" color="black">
                        {nodeData?._Associations?.Resurs1?.RESURS_NAMN}
                      </EDTypography>
                    </ThemeProvider>
                  </StyledEngineProvider>
                </ObjektVyKort>
                <AttributGrupp cardInCard>
                  {digital ? ( // Digital aktivitet
                    <>
                      {attributeCounter(
                        avenyCollectionMap,
                        formContext,
                        'DigitalaVisningar.VISNINGAR_TOTAL',
                        onDigitalCounterChange,
                        { disabled: true, hideButtons: true, numericOnly: true }
                      )}
                      {attributeCounter(
                        avenyCollectionMap,
                        formContext,
                        'DigitalaVisningar.VISNINGAR_FACEBOOK',
                        onDigitalCounterChange,
                        {
                          disabled: disabled,
                          numericOnly: true,
                          testId: 'facebook-visningar-counter',
                        }
                      )}
                      {attributeCounter(
                        avenyCollectionMap,
                        formContext,
                        'DigitalaVisningar.VISNINGAR_YOUTUBE',
                        onDigitalCounterChange,
                        {
                          disabled: disabled,
                          numericOnly: true,
                          testId: 'youtube-visningar-counter',
                        }
                      )}
                      {attributeCounter(
                        avenyCollectionMap,
                        formContext,
                        'DigitalaVisningar.VISNINGAR_VIMEO',
                        onDigitalCounterChange,
                        { disabled: disabled, numericOnly: true }
                      )}
                      {attributeCounter(
                        avenyCollectionMap,
                        formContext,
                        'DigitalaVisningar.VISNINGAR_OVRIG',
                        onDigitalCounterChange,
                        { disabled: disabled, numericOnly: true }
                      )}
                    </>
                  ) : (
                    <>
                      {attributeCounter(
                        avenyCollectionMap,
                        formContext,
                        'ANTAL_BESOKARE',
                        onAttrCounterChange,
                        {
                          disabled: disabled || aldersindelad,
                          numericOnly: true,
                          label: verksamhetstypLabelText(verksamhetsKategori),
                          tooltip: verksamhetstypHintText(verksamhetsKategori),
                          hideButtons: aldersindelad,
                        }
                      )}
                      {!queryLoading && (
                        <>
                          {nodeData?._typ !== TEDBOAktivitetTypEnum.AKTOVRIGT &&
                            gudstjanstKategori !== '3b' &&
                            gudstjanstKategori !== '3h' &&
                            attributeCounter(
                              avenyCollectionMap,
                              formContext,
                              'ANTAL_BARN',
                              onAttrCounterChange,
                              { disabled: disabled, numericOnly: true }
                            )}

                          {nodeData?._typ === TEDBOAktivitetTypEnum.AKTOVRIGT &&
                            verksamhetsKategori === '8s' &&
                            attributeCounter(
                              avenyCollectionMap,
                              formContext,
                              'ANTAL_TILLFALLEN',
                              onAttrCounterChange,
                              {
                                disabled: disabled,
                                label: 'Antal besökta andra grupper',
                                numericOnly: true,
                              }
                            )}
                          {((aktType &&
                            validAktTypeForKomm.includes(aktType)) ||
                            validGtjCategoriForKomm.includes(
                              gudstjanstKategori
                            )) &&
                            attributeCounter(
                              avenyCollectionMap,
                              formContext,
                              'ANTAL_KOMMUNIONER',
                              onAttrCounterChange,
                              {
                                disabled: disabled,
                                numericOnly: true,
                              }
                            )}
                          {aldersindelad &&
                            alderCounters(
                              aktGiltigaAldrar,
                              formContext,
                              disabled,
                              true
                            )}
                        </>
                      )}
                    </>
                  )}
                </AttributGrupp>
                {activityTypeHasPsalmer &&
                  formContext?.formState?.currentValues?._Associations
                    ?.AktPsalmer_All && (
                    <Psalmer
                      formContext={formContext}
                      activityType={nodeData?._typ}
                      isBegravningAndIsSvKyrkanBegrType={
                        isBegravningAndIsSvKyrkanBegrType
                      }
                      isNotWithinValidDateRange={disabled}
                    />
                  )}
              </ObjektVyKort>
            )}
          </BusinessDataFormContext.Consumer>
        </ObjektVySida>
      </ObjektVy>
    </BusinessDataForm>
  );
};

export const statistik = collectionCompToObjektVy(
  StatistikrapporteringVy,
  'statistik'
);
